
import {computed, ComputedRef, defineComponent, onMounted, ref, Ref, watch} from "vue";
import {useStore} from "vuex";
import {Content} from "./helpers";
import {Instructor} from "../../../../domain/entities/entities";
import {dispatchError} from "../../utils/error_handling";
import {ContentType} from "../../../../domain/entities/content/content";
import {onBeforeRouteLeave} from "vue-router";

export default defineComponent({
  name: "MP3Viewer",
  setup() {
    const store = useStore();

    onMounted(async () => await onCurrentContentUpdate());

    const currentContent: ComputedRef<Content> = computed(
        () => store.getters["content_module/currentContent"]
    );

    watch(currentContent, async () => {
      await onCurrentContentUpdate();
    });

    /** on current content change clear all previous routines, load the player and start a new progress routine. */
    const onCurrentContentUpdate = async (): Promise<void> => {
      try {
        await stopCheckpointRoutine();
        await stopProgressRoutine();
        if (currentContent.value.type !== ContentType.MP3) return;
        await loadPlayer();
        await startProgressRoutine();
        await loadInstructors();
      } catch (e) {
        console.error("[onCurrentContentUpdate] Error loadInstructors ", e);
        await dispatchError(e);
      }
    };

    const audio: Ref<HTMLAudioElement | null> = ref(null);

    const loadPlayer = (): void => {
      if (!audio.value) return;
      audio.value.load();
      audio.value.currentTime = currentContent.value.status.secondsCheckpoint?.timestamp ?? 0;
      audio.value.onloadeddata = onPlayerIsReady;
      audio.value.onplay = onAudioPlay;
      audio.value.onpause = onAudioPause;
      audio.value.onratechange = onRateChange;
    };

    /** Play the audio data.
     * It does not dispatch the error box because the only reasons to get an error are the following:
     * DOMException: failed because the user didn't interact with the document first. - Occurs when the user refresh the page.
     * @see HTMLMediaElement.play */
    const onPlayerIsReady = async (): Promise<void> => {
      try {
        await audio.value?.play();
      } catch (e) {
        console.warn("[onPlayerIsReady] Error play ", e);
      }
    };

    const onRateChange = async (): Promise<void> => {
      try {
        await updatePlaybackRate();
      } catch (e) {
        console.error("[onRateChange] Error updatePlaybackRate ", e);
        await dispatchError(e);
      }
    };

    const updatePlaybackRate = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/player/setPlaybackRate", audio.value?.playbackRate);
      } catch (e) {
        console.error("[updatePlaybackRate] Error dispatch ", e);
        throw e;
      }
    };

    const onAudioPlay = async (): Promise<void> => {
      try {
        await startCheckpointRoutine();
      } catch (e) {
        console.error("[onAudioPlay] Error startCheckpointRoutine ", e);
        await dispatchError(e);
      }
    };

    let checkpointInterval;
    const startCheckpointRoutine = (): void => {
      checkpointInterval = setInterval(saveCheckpointSeconds, 5000);
    };

    const saveCheckpointSeconds = async (): Promise<void> => {
      try {
        if (!audio.value) return;
        const timestamp = Math.round(audio.value.currentTime);
        await store.dispatch("content_module/player/checkpoint/saveCheckpointSeconds", timestamp);
      } catch (e) {
        console.error("[saveCheckpointSeconds] Error saveCheckpointSeconds ", e);
        await dispatchError(e);
      }
    }

    const onAudioPause = async (): Promise<void> => {
      try {
        await stopCheckpointRoutine();
      } catch (e) {
        console.error("[onAudioPause] Error stopProgressRoutine ", e);
        await dispatchError(e);
      }
    };

    const stopCheckpointRoutine = async (): Promise<void> => {
      try {
        clearInterval(checkpointInterval);
      } catch (e) {
        console.error("[stopCheckpointRoutine] Error dispatch ", e);
        throw e;
      }
    };

    const startProgressRoutine = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/player/progress/startProgressRoutine");
      } catch (e) {
        console.error("[startProgressRoutine] Error dispatch ", e);
        throw e;
      }
    };

    const stopProgressRoutine = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/player/progress/stopProgressRoutine");
      } catch (e) {
        console.error("[stopProgressRoutine] Error dispatch ", e);
        throw e;
      }
    };

    const loadInstructors = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/loadInstructors");
      } catch (e) {
        console.error("[loadInstructors] Error dispatch", e);
        throw e;
      }
    };

    const instructors: ComputedRef<Instructor[]> = computed(
        () => store.getters["content_module/instructors"]
    );

    onBeforeRouteLeave(async () => {
      await stopCheckpointRoutine();
      await stopProgressRoutine();
    });

    return {
      audio,
      currentContent,
      instructors,
    };
  }
});
